﻿import React, { useState, useEffect } from 'react';
import GlobalDialog from './Modal/GlobalDialog';
import InstrumentSelectBoxTooltip from './InstrumentSelectBoxTooltip';
import InstrumentInformation from './InstrumentInformation';
import { Asset } from '../models/asset';
import { BenchSheetTechnologyType, VmiGroups } from '../models/enums';
import { FieldUpdate } from '../models/field-update';
import { ModalMetadata } from '../models/modal-metadata';
import { ModalMessageType } from '../models/enums';
import { DataTemplates } from '../templates/data-templates';
import { CrudFunctions } from '../utilities/crud-functions';
import LogbookEntryComponent from './Common/LogBookEntryComponent';
import { OpticalRotationBenchSheet } from '../models/optical-rotation-bench-sheet';

import SelectBox from 'devextreme-react/select-box';
import Button from 'devextreme-react/button';
import { Popup as DialogPopup } from 'devextreme-react/popup';

export interface Props {
    benchSheet: OpticalRotationBenchSheet;
    readOnly?: boolean;
}

export default function OpticalRotationInstruments(props: Props) {
    const [balances, setBalances] =
        useState<Asset[]>();

    const [selectedBalance, setSelectedBalance] =
        useState<Asset>();

    const [ovens, setOvens] =
        useState<Asset[]>();

    const [selectedOven, setSelectedOven] =
        useState<Asset>();

    const [polarimeters, setPolarimeters] =
        useState<Asset[]>();

    const [selectedPolarimeter, setSelectedPolarimeter] =
        useState<Asset>();

    const [selectedLogbookAsset, setSelectedLogbookAsset] =
        useState<Asset>();

    const [logbookEntryDialogVisible, setLogbookEntryDialogVisible] =
        useState<boolean>();

    const [globalDialogMetadata, setGlobalDialogMetadata] =
        useState<ModalMetadata>();

    const [globalDialogVisible, setGlobalDialogVisible] =
        useState<boolean>();

    const [dataLoaded, setDataLoaded] =
        useState<boolean>();

    useEffect(() => {
        if (!dataLoaded)
            getAllData();
    }, [props.benchSheet]);

    return (
        <div>
            <div className="lq-header pw-margin-top-05">
                <div className="lq-header-title">Instruments</div>
            </div>

            {dataLoaded && balances && ovens && polarimeters &&
                <div className="pw-form-section">
                    <div className="pw-comp-row">
                        <div className="pw-form-unit pw-comp-20">
                            <div className="pw-form-unit-label">Balance</div>
                            <SelectBox
                                elementAttr={{ class: "balance-selectbox" }}
                                readOnly={props.readOnly}
                                placeholder='Balance'
                                className="asset-selectbox"
                                height='1.8rem'
                                itemRender={DataTemplates.InstrumentItemRender}
                                dataSource={balances ? balances : []}
                                onValueChanged={onBalanceChanged}
                                valueExpr={'assetId'}
                                displayExpr={(item) => {
                                    return item ? `${item.formattedAssetNumber} - ${item.assetName ?? "NA"}` : "";
                                }}
                                value={selectedBalance?.assetId} />
                            <InstrumentSelectBoxTooltip target=".balance-selectbox" instrument={selectedBalance} />
                        </div>

                        <div className="pw-row-button-08">
                            <Button text="Logbook" icon="edit" type="success"
                                disabled={!selectedBalance}
                                onClick={() => handleLogbookOpenClick(selectedBalance)} />
                        </div>

                        <InstrumentInformation instrument={selectedBalance} />
                    </div>

                    <div className="pw-comp-row">
                        <div className="pw-form-unit pw-comp-20">
                            <div className="pw-form-unit-label">Oven</div>
                            <SelectBox
                                elementAttr={{ class: "oven-selectbox" }}
                                readOnly={props.readOnly}
                                placeholder='Oven'
                                className="asset-selectbox"
                                height='1.8rem'
                                itemRender={DataTemplates.InstrumentItemRender}
                                dataSource={ovens ? ovens : []}
                                onValueChanged={onFurnaceChanged}
                                valueExpr='assetId'
                                displayExpr={(item) => {
                                    return item ? `${item.formattedAssetNumber} - ${item.assetName ?? "NA"}` : "";
                                }}
                                value={selectedOven?.assetId} />
                            <InstrumentSelectBoxTooltip target=".oven-selectbox" instrument={selectedOven} />
                        </div>

                        <div className="pw-row-button-08">
                            <Button text="Logbook" icon="edit" type="success"
                                disabled={!selectedOven}
                                onClick={() => handleLogbookOpenClick(selectedOven)} />
                        </div>

                        <InstrumentInformation instrument={selectedOven} />
                    </div>

                    <div className="pw-comp-row">
                        <div className="pw-form-unit pw-comp-20">
                            <div className="pw-form-unit-label">Polarimeter</div>
                            <SelectBox
                                elementAttr={{ class: "polarimeter-selectbox" }}
                                readOnly={props.readOnly}
                                placeholder='Polarimeter'
                                className="asset-selectbox"
                                height='1.8rem'
                                itemRender={DataTemplates.InstrumentItemRender}
                                dataSource={polarimeters ? polarimeters : []}
                                onValueChanged={onPolarimeterChanged}
                                valueExpr='assetId'
                                displayExpr={(item) => {
                                    return item ? `${item.formattedAssetNumber} - ${item.assetName ?? "NA"}` : "";
                                }}
                                value={selectedPolarimeter?.assetId} />
                            <InstrumentSelectBoxTooltip target=".polarimeter-selectbox" instrument={selectedPolarimeter} />
                        </div>

                        <div className="pw-row-button-08">
                            <Button text="Logbook" icon="edit" type="success"
                                disabled={!selectedPolarimeter}
                                onClick={() => handleLogbookOpenClick(selectedPolarimeter)} />
                        </div>

                        <InstrumentInformation instrument={selectedPolarimeter} />
                    </div>
                </div>
            }

            {logbookEntryDialogVisible &&
                <DialogPopup visible={logbookEntryDialogVisible}
                    onHiding={hideLogbookEntryDialog}
                    title="Logbook Entry"
                    width='80vw'
                    height='80vh'
                    dragEnabled={false}
                    hideOnOutsideClick={false}
                    resizeEnabled={false}>
                    <LogbookEntryComponent asset={selectedLogbookAsset}
                        benchSheetId={props.benchSheet.benchSheetId}
                        onClose={hideLogbookEntryDialog} />
                </DialogPopup>
            }

            {
                globalDialogVisible &&
                <GlobalDialog metadata={globalDialogMetadata} onClose={clearGlobalDialog} />
            }

        </div >
    );

    async function getBalances() {
        await CrudFunctions.getData("assets/allbalances")
            .then(data => {
                setBalances(data);
                const selectedBalance = data.find(i => i.assetId === props.benchSheet?.balance?.assetId);
                if (selectedBalance)
                    setSelectedBalance(selectedBalance);
            })
            .catch((res) => {
                showMessage(res, ModalMessageType.Error, "Error Retrieving Balances");
            });
    }

    async function getOvens() {
        await CrudFunctions.getData(`assets/assets?technologyType=${BenchSheetTechnologyType.OpticalRotation}&vmiGroup=${VmiGroups.DryingOven}`)
            .then(data => {
                setOvens(data);
                const selectedOven = data.find(i => i.assetId === props.benchSheet?.oven?.assetId);
                if (selectedOven)
                    setSelectedOven(selectedOven);
            })
            .catch((res) => {
                showMessage(res, ModalMessageType.Error, "Error Retrieving Ovens");
            });
    }

    async function getPolarimeters() {
        await CrudFunctions.getData(`assets/assets?technologyType=${BenchSheetTechnologyType.OpticalRotation}&vmiGroup=${VmiGroups.Polarimeter}`)
            .then(data => {
                setPolarimeters(data);
                const selectedPolarimeter = data.find(i => i.assetId === props.benchSheet?.polarimeter?.assetId);
                if (selectedPolarimeter)
                    setSelectedPolarimeter(selectedPolarimeter);
            })
            .catch((res) => {
                showMessage(res, ModalMessageType.Error, "Error Retrieving Polarimeters");
            });
    }

    async function getAllData() {
        await Promise.all([
            getBalances(),
            getOvens(),
            getPolarimeters()
        ])
            .then(() => setDataLoaded(true));
    }

    async function onBalanceChanged(e: any) {
        if (!e.event)
            return;

        if (balances && e.value) {
            let selectedItem = balances.find(b => b.assetId === e.value);

            if (selectedItem) {
                setSelectedBalance(selectedItem);
                let fieldUpdate: FieldUpdate = {
                    entity: "OpticalRotationBenchSheets",
                    field: "balanceId",
                    value: e.value,
                    rowId: props.benchSheet.benchSheetId
                };

                await updateField(fieldUpdate)
                    .catch((res) => {
                        showMessage(res, ModalMessageType.Error, "Error Updating BalanceId");
                    });
            }
        }
    }

    async function onFurnaceChanged(e: any) {
        if (!e.event)
            return;

        if (ovens && e.value) {
            let selectedItem = ovens.find(i => i.assetId === e.value);

            if (selectedItem) {
                setSelectedOven(selectedItem)

                let fieldUpdate: FieldUpdate = {
                    entity: "OpticalRotationBenchSheets",
                    field: "ovenId",
                    value: e.value,
                    rowId: props.benchSheet.benchSheetId
                };

                await updateField(fieldUpdate)
                    .catch((res) => {
                        showMessage(res, ModalMessageType.Error, "Error Updating OvenId");
                    });
            }
        }
    }

    async function onPolarimeterChanged(e: any) {
        if (!e.event)
            return;

        if (polarimeters && e.value) {
            let selectedItem = polarimeters.find(i => i.assetId === e.value);

            if (selectedItem) {
                setSelectedPolarimeter(selectedItem)

                let fieldUpdate: FieldUpdate = {
                    entity: "OpticalRotationBenchSheets",
                    field: "polarimeterId",
                    value: e.value,
                    rowId: props.benchSheet.benchSheetId
                };

                await updateField(fieldUpdate)
                    .catch((res) => {
                        showMessage(res, ModalMessageType.Error, "Error Updating PolarimeterId");
                    });
            }
        }
    }

    async function updateField(fieldUpdate: FieldUpdate) {
        return CrudFunctions.putData("benchsheets/orbenchsheetfield", fieldUpdate);
    }

    function handleLogbookOpenClick(asset) {
        if (!asset) return;
        setSelectedLogbookAsset(asset);
        setLogbookEntryDialogVisible(true);
    }

    function hideLogbookEntryDialog() {
        setLogbookEntryDialogVisible(false);
    }

    function showMessage(res: any, mode: ModalMessageType, title: string) {
        let mm: ModalMetadata = {
            error: res,
            mode: mode,
            title: title
        };

        setGlobalDialogMetadata(mm);
        setGlobalDialogVisible(true);
    }

    function clearGlobalDialog() {
        setGlobalDialogMetadata(null);
        setGlobalDialogVisible(false);
    }

}
